/** Definitions for create advanced script flow. At time of writing, these steps only represent the "with CCI" flow,
 *  but the intent is to add a definition that supports the legacy flow.
 */
// constants
import { activityTypes } from 'constants/activities';
import { advancedScriptRequirements } from 'constants/advanced';
import {
  clearExcludedLayers,
  clearHarvestHistory,
  initHarvestHistory
} from 'advanced';
import { applicationTypes, scriptMethods } from 'constants/scripts';
import { inputStates } from 'constants/values';
import { resourceType } from 'constants/loading';
import { errorLevel } from 'constants/errors';

// requesters
import { requestActivities } from 'activities/requesters';

// selectors
import { getActiveFieldUuid, getActiveFieldCountryCode } from 'current/selectors';
import {
  doesHarvestHistorySelectionMeetRequirements,
  getAdvancedScriptRequirementsMatch,
  getRecommendedNumZones,
  getExcludedLayersParam,
  getDefaultAgronomicSeason
} from 'advanced/selectors';
import {
  getPendingResourcePromise,
  isResourceTypeLoaded,
  isResourceTypePending,
  isResourceTypeUnloaded
} from 'loading/selectors';
import { getPlantingPlanParams } from 'planting-plan-form/selectors';
import { getActiveScenarioUuid, getDiscardedScenarioUuids } from 'advanced/scenarios/selectors';
import { getStepNumber } from 'script-flow/selectors';
import { getDefaultDateForActivityType } from 'activities/selectors';
import { getActiveCropId } from 'current/currentSelectors';
import { getCropDfpUuidByLegacyId, getNominalWeightByCropId } from 'crops/selectors';

// finders
import { findAdvancedScriptAvailability } from 'advanced/finders';

// actions
import {
  loadNewAdvancedScript,
  startNewZoneGeneration,
  removeScripts,
  setScriptState,
  snapshotSoilComposition
} from 'scripts';
import { clearScenarios } from 'advanced/scenarios';
import { setActiveScriptId, setActiveScriptMethod } from 'current';
import {
  initializePlantingPlanParams,
  resetReviewScenarioPlantingPlanParams,
  resetPlantingPlanParams
} from 'planting-plan-form';
import {
  setMinimumRequirementsErrorModal,
  setSeedProductInput
} from 'current/uiwidgets';
import { endPendingResourcesOfType } from 'loading';
import { setApplicationProducts } from 'products';


// util
import { getAdvancedScriptingRoute, getCreateRoute, getEditRouteForScriptId } from 'util/routes';
import { treatErrorAs } from 'api/errors';
import { ignorePollingAbort } from 'api/shared';
import { convertForLocale } from 'util/unitConversion';
import { formatNumberRoundToNearest } from 'util/formatters/numberFormatter';

const _getPlantingPlanParams = (state, cropPlan) => {
  const plantingPlanParams = getPlantingPlanParams(state);
  const numZones = getRecommendedNumZones(state);
  const countryCode = getActiveFieldCountryCode(state);
  const agronomicSeason = getDefaultAgronomicSeason(state);
  const cropNominalWeight = getNominalWeightByCropId(state, cropPlan?.cropId);

  let cropPlanData;

  if (cropPlan) {
    const { seedingRate, targetYield, seed } = cropPlan;
    const convertedSeedingRate = seedingRate && convertForLocale({
      quantity: seedingRate.value,
      unit: seedingRate.unit,
      unitType: 'seed-density'
    });
    const convertedTargetYield = targetYield && convertForLocale({
      quantity: targetYield.value,
      unit: targetYield.unit,
      unitType: 'area-density.yield',
      cropNominalWeight
    });
    cropPlanData = {
      currentAveragePopulation: formatNumberRoundToNearest(convertedSeedingRate?.scalar, 1),
      ['target-yield']: convertedTargetYield?.scalarLocaleString,
      productId: seed?.id
    };
  }

  return {
    date: cropPlan?.plannedDate || getDefaultDateForActivityType(state, activityTypes.PLANTING),
    ['num-zones']:numZones,
    agronomicSeason,
    ...cropPlanData,
    ...plantingPlanParams, // Overrides above default values if user has picked something.
    activityCategoryUuid: activityTypes.PLANTING,
    activityType: activityTypes.PLANTING,
    countryCode
  };
};

const _setSeedFromCropPlan = ({ dispatch, cropPlan }) => {
  const { seed } = cropPlan;
  const product = { ...seed, uuid: seed.id };
  
  dispatch(setApplicationProducts({
    applicationType: applicationTypes.SEED,
    products: [product]
  }));
  dispatch(setSeedProductInput({ 
    currentProductId: seed.id,
    products: [product],
    selectId: 'select-1'
  }));
};


/* Cleanup should be performed when exiting the flow, even though not strictly necessary. It helps to declutter the
   store and catch debug errors where code may incorrectly depend on the data being present post-flow. Since it's
   possible to exit the flow without cleanup (e.g. the browser closes) cleanup should also be performed when the flow
   starts. */
const _cleanupFlow = (state, dispatch) => {
  dispatch(removeScripts(getDiscardedScenarioUuids(state)));
  dispatch(endPendingResourcesOfType({ type: resourceType.ADVANCED_SCRIPT_AVAILABILITY }));
  dispatch(endPendingResourcesOfType({ type: resourceType.ADVANCED_SCRIPT_RECOMMENDATIONS }));
  dispatch(clearScenarios());
  dispatch(clearHarvestHistory());
  dispatch(clearExcludedLayers());
  dispatch(resetPlantingPlanParams());
};

const _initFlow = (state, dispatch) => {
  _cleanupFlow(state, dispatch);
  dispatch(initHarvestHistory());
};

// Step Actions - don't call directly. Instead add to script flow definition and use script flow navigation to call.

export const onStep0Create = (dispatch, getState) => {
  const state = getState();
  _cleanupFlow(state,dispatch);
  return {route:getCreateRoute(getState())};
};

const requirementsMatchToStep1TitleText = {
  [advancedScriptRequirements.MEETS_WITH_YIELD_ONLY]:'title-text__create-advanced-planting-script--step-1',
  [advancedScriptRequirements.MEETS_WITH_CCI]:'title-text__create-advanced-planting-script--step-1b',
  [advancedScriptRequirements.DOES_NOT_MEET]:'title-text__create-advanced-planting-script--step-1c',
  [advancedScriptRequirements.PENDING]:'title-text__create-advanced-planting-script--step-1'
};

const onceAdvancedScriptAvailabilityLoaded = ({ dispatch, getState, fromStepNumber }) => {
  // setting this here to make sure it's available to initialize/validate planting
  // plan params before the route is entereed.
  dispatch(setActiveScriptMethod(scriptMethods.ADVANCED));
  const state = getState();
  const activeCropUuid = getCropDfpUuidByLegacyId(state, getActiveCropId(state));
  const cropPlan = state.current?.cropPlan;
  const seed = cropPlan?.seed;
  const isCropPlanAvailable = cropPlan?.cropId === activeCropUuid;

  // State may be updating at each step
  const plantingPlanParams = _getPlantingPlanParams(state, (isCropPlanAvailable && cropPlan));
  dispatch(initializePlantingPlanParams({plantingPlanParams}));

  if (isCropPlanAvailable && seed) {
    _setSeedFromCropPlan({ dispatch, cropPlan });
  }

  if (fromStepNumber === 0) { _initFlow(getState(), dispatch); }
};

export const onStep1ReviewHarvestHistory = (dispatch, getState) => {
  const state = getState();
  const fromStepNumber = getStepNumber(state);
  const requirementsMatch = getAdvancedScriptRequirementsMatch(state);
  const titleTextMessageKey = requirementsMatchToStep1TitleText[requirementsMatch];
  const route = getAdvancedScriptingRoute(state, 1);
  const fieldUuid = getActiveFieldUuid(state);

  dispatch(requestActivities());

  const type = resourceType.ADVANCED_SCRIPT_AVAILABILITY;

  // We may have loaded the availability data earlier
  // But couldn't use it because we didn't know if the user
  // was entering adv. planting script creation,
  // so now we have to handle the various cases
  //
  // if we dont have the data and no call is pending, kick off a call
  if( isResourceTypeUnloaded({ state, type }) ) {

    findAdvancedScriptAvailability({ dispatch, getState, fieldUuid })
    .then( () => onceAdvancedScriptAvailabilityLoaded({ dispatch, getState, fromStepNumber }) )
    // Abort means the user did something to cancel polling, so not an error, not success
    .catch( ignorePollingAbort() )
    .catch( treatErrorAs({ severity: errorLevel.WARN, andHandleNow: true, message: 'find/process availability' }) );

    // if the data is loaded, reset what state may have changed or was never set
  } else if(isResourceTypeLoaded({ state, type })) {
    onceAdvancedScriptAvailabilityLoaded({ dispatch, getState, fromStepNumber });

  // if the data is requested but not yet loaded, let's attach the needed processing to the pending promise
  // now that we know the user is doing Adv. Planting Scripting
  } else if(isResourceTypePending({ state, type })) {
    const pendingPromise = getPendingResourcePromise({ state, type });
    if(pendingPromise) {
      pendingPromise.then( () => onceAdvancedScriptAvailabilityLoaded({ dispatch, getState, fromStepNumber }) )
      // Abort means the user did something to cancel polling, so not an error, not success
      .catch( ignorePollingAbort() )
      .catch( treatErrorAs({ severity: errorLevel.WARN, andHandleNow: true, message: 'find/process availability' }) );
    }
  }
  return { titleTextMessageKey, route };
};

export const onStep2EnterPlantingPlan = (dispatch, getState) => {
  const state = getState();
  const fromStepNumber = getStepNumber(state);

  if ( fromStepNumber === 1 && !doesHarvestHistorySelectionMeetRequirements(state) ) {
    dispatch(setMinimumRequirementsErrorModal());
    return null;
  } else if( fromStepNumber === 1 && getExcludedLayersParam(state).length) {
    // Zone generation can take a while, so we kick it off before they actually get there
    dispatch(startNewZoneGeneration());
  }

  if (fromStepNumber === 3) {
    dispatch(resetReviewScenarioPlantingPlanParams());
    dispatch(clearScenarios());
  }

  const route = getAdvancedScriptingRoute(state, 2);
  return {route, titleTextMessageKey:'title-text__create-advanced-planting-script--step-2' };
};

export const onStep3ReviewScript = (dispatch, getState) => {
  const state = getState();
  dispatch(loadNewAdvancedScript());
  const route = getAdvancedScriptingRoute(state, 3);
  return {route, titleTextMessageKey:'title-text__create-advanced-planting-script--step-3' };
};

export const onStep4FinishFlow = (dispatch, getState) => {
  const state = getState();
  const scenarioUuid = getActiveScenarioUuid(state);
  const route = getEditRouteForScriptId(state, scenarioUuid);
  _cleanupFlow(state,dispatch);
  dispatch(setActiveScriptId(scenarioUuid));
  dispatch(setScriptState({ scriptId:scenarioUuid, scriptState:inputStates.CHANGED }));
  dispatch(snapshotSoilComposition(scenarioUuid));
  return {route};
};
